import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import FilePicker from "components/FilePicker";
import { useSnackbar } from "hooks/useSnackbar";
import { uploadContentTranslations } from "services/uploadService";
import sessionHelper from "services/sessionHelper";
import * as XLSX from "xlsx";

export interface TranslationField {
  /** Display label shown as the sheet name, e.g. "Title", "Body" */
  label: string;
  /** The backend field key, e.g. "titleTrans", "bodyTrans", "nameTrans" */
  fieldName: string;
  /** Optional custom dummy text for the template. When set, this replaces the default per-language placeholder. */
  dummyText?: string;
}

export interface TranslationPreviewRow {
  fieldName: string;
  languageCode: string;
  description: string;
  errors: string[];
}

interface PreviewRow {
  languageCode: string;
  description: string;
  errors: string[];
}

interface FieldPreview {
  field: TranslationField;
  rows: PreviewRow[];
  sheetMissing?: boolean;
}

interface BulkTranslationUploadProps {
  assetType: string;
  assetId?: string;
  /** Translatable fields — one sheet per field in the template */
  fields: TranslationField[];
  disabled?: boolean;
  /** Called after a successful upload */
  onSuccess?: () => void;
  /** When true the component only collects the file; upload is handled by the parent after create */
  deferUpload?: boolean;
  /** Fires whenever the selected file changes (useful in deferred mode) */
  onFileChange?: (file: File | null) => void;
  /** Fires whenever the parsed/validated preview changes — lets a parent consume rows before an assetId exists */
  onPreviewChange?: (rows: TranslationPreviewRow[], hasErrors: boolean) => void;
  /** Optional per-row validation hook (e.g. placeholder-count rules) — returning a message flags the row as an error */
  validateRow?: (fieldName: string, languageCode: string, description: string) => string | null | undefined;
}

const useStyles = makeStyles((theme) => ({
  card: {
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "#ffffff",
    transition: "box-shadow 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    "& svg": {
      fontSize: "1.5rem",
      color: theme.palette.primary.main,
    },
  },
  infoBox: {
    padding: "12px 16px",
    borderRadius: "8px",
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    "& svg": {
      color: "#1976d2",
    },
  },
  uploadSection: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 16,
    alignItems: "flex-end",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  errorRow: {
    backgroundColor: "#fdecea",
  },
}));

/** Parse a workbook into one preview per configured field, matching sheets by label (case-insensitive). */
const parseWorkbook = async (
  file: File,
  fields: TranslationField[],
  validCodes: Set<string>,
  validateRow?: (fieldName: string, languageCode: string, description: string) => string | null | undefined,
): Promise<{ previews: FieldPreview[]; parseError: string | null }> => {
  let workbook: XLSX.WorkBook;
  try {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(buffer, { type: "array" });
  } catch {
    return {
      previews: [],
      parseError: "Could not read the selected file. Make sure it's a valid .xlsx file.",
    };
  }

  if (!workbook.SheetNames.length) {
    return { previews: [], parseError: "The uploaded file contains no sheets." };
  }

  const previews: FieldPreview[] = [];
  const missingSheets: string[] = [];

  for (const field of fields) {
    const sheetName = workbook.SheetNames.find(
      (name) => name.toLowerCase() === field.label.toLowerCase(),
    );

    if (!sheetName) {
      missingSheets.push(field.label);
      previews.push({ field, rows: [], sheetMissing: true });
      continue;
    }

    const sheet = workbook.Sheets[sheetName];
    const jsonRows = XLSX.utils.sheet_to_json<Record<string, string>>(sheet);

    const codeCounts = new Map<string, number>();
    const rows: PreviewRow[] = jsonRows.map((row) => {
      const languageCode = (row["Locale"] ?? "").toString().trim();
      const description = (row["Text"] ?? "").toString().trim();
      if (languageCode) {
        codeCounts.set(languageCode, (codeCounts.get(languageCode) || 0) + 1);
      }
      return { languageCode, description, errors: [] };
    });

    rows.forEach((row) => {
      if (!row.languageCode) row.errors.push("Missing Locale");
      if (!row.description) row.errors.push("Missing Text");
      if (row.languageCode && !validCodes.has(row.languageCode)) {
        row.errors.push(`Unrecognised Locale "${row.languageCode}"`);
      }
      if (row.languageCode && (codeCounts.get(row.languageCode) || 0) > 1) {
        row.errors.push("Duplicate Locale");
      }
      if (row.description && validateRow) {
        const customError = validateRow(field.fieldName, row.languageCode, row.description);
        if (customError) row.errors.push(customError);
      }
    });

    previews.push({ field, rows });
  }

  const parseError = missingSheets.length
    ? `Missing required sheet(s): ${missingSheets.join(", ")}.`
    : null;

  return { previews, parseError };
};

const previewHasErrors = (previews: FieldPreview[] | null, parseError: string | null): boolean => {
  if (parseError) return true;
  if (!previews) return false;
  return previews.some((fp) => fp.sheetMissing || fp.rows.some((row) => row.errors.length > 0));
};

const toPreviewRows = (previews: FieldPreview[]): TranslationPreviewRow[] =>
  previews.flatMap((fp) =>
    fp.rows.map((row) => ({
      fieldName: fp.field.fieldName,
      languageCode: row.languageCode,
      description: row.description,
      errors: row.errors,
    })),
  );

const BulkTranslationUpload = ({
  assetType,
  assetId,
  fields,
  disabled = false,
  onSuccess,
  deferUpload = false,
  onFileChange,
  onPreviewChange,
  validateRow,
}: BulkTranslationUploadProps) => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<FieldPreview[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const hasErrors = useMemo(() => previewHasErrors(preview, parseError), [preview, parseError]);

  const languageNameByCode = useMemo(() => {
    const map: Record<string, string> = {};
    (sessionHelper.languages || []).forEach((lang: { isoCode: string; name: string }) => {
      map[lang.isoCode] = lang.name;
    });
    return map;
  }, []);

  const resetPreview = () => {
    setPreview(null);
    setParseError(null);
  };

  const onSelectFile = async (selectedFile: File | null) => {
    setFile(selectedFile);
    onFileChange?.(selectedFile);

    if (!selectedFile) {
      resetPreview();
      onPreviewChange?.([], false);
      return;
    }

    setParsing(true);
    const languages: { isoCode: string; name: string }[] = sessionHelper.languages || [];
    const validCodes = new Set(languages.map((lang) => lang.isoCode));
    const { previews, parseError: nextParseError } = await parseWorkbook(
      selectedFile,
      fields,
      validCodes,
      validateRow,
    );
    setPreview(previews);
    setParseError(nextParseError);
    setParsing(false);
    onPreviewChange?.(toPreviewRows(previews), previewHasErrors(previews, nextParseError));
  };

  const onCancel = () => {
    setFile(null);
    resetPreview();
    onFileChange?.(null);
    onPreviewChange?.([], false);
  };

  const onUpload = async () => {
    if (!file || hasErrors) return;
    try {
      setLoading(true);
      const response = await uploadContentTranslations(file, assetType, assetId);
      setLoading(false);
      setFile(null);
      resetPreview();
      if (!response.status) throw new Error(response?.message);
      showSnackbar(response.message || "Translations uploaded successfully");
      onSuccess?.();
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const onDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Get all language codes from session
    const languages: { isoCode: string; name: string }[] =
      sessionHelper.languages || [];

    for (const field of fields) {
      const rows = languages.map((lang) => ({
        Locale: lang.isoCode,
        Text: field.dummyText || `Enter "${lang.name}" text here`,
        _fieldName: field.fieldName,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      // Set column widths for readability
      ws["!cols"] = [{ wch: 12 }, { wch: 45 }, { wch: 20 }];

      XLSX.utils.book_append_sheet(wb, ws, field.label);
    }

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${assetType.toLowerCase()}_translation_template.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fieldLabels = fields.map((f) => f.label).join(", ");

  return (
    <Card className={styles.card}>
      <Box className={styles.sectionHeader}>
        <TranslateIcon />
        <Typography variant="h6" fontWeight={600}>
          Bulk Translation Upload
        </Typography>
      </Box>
      <Box className={styles.infoBox}>
        <InfoOutlinedIcon fontSize="small" />
        <Typography variant="body2">
          Upload an Excel file (.xlsx) to update translations for{" "}
          <strong>{fieldLabels}</strong>. Download the template first — it
          contains one sheet per field with a <strong>Locale</strong> and{" "}
          <strong>Text</strong> column for every supported language.
        </Typography>
      </Box>
      <Box className={styles.uploadSection}>
        <Box flex={1}>
          <FilePicker
            label="Select Translation File"
            name="bulk-translation"
            fileType=".csv, .xlsx, .xls"
            value={file}
            showPreview={false}
            onFileSelect={onSelectFile}
          />
        </Box>
        <Box className={styles.actions}>
          <Button
            variant="outlined"
            onClick={onDownloadTemplate}
            startIcon={<DownloadIcon />}
            size="large"
          >
            Download Template
          </Button>
        </Box>
      </Box>

      {file && (
        <Box sx={{ mt: 3 }}>
          {parsing && (
            <Typography variant="body2" color="text.secondary">
              Validating file…
            </Typography>
          )}

          {!parsing && parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {!parsing &&
            preview &&
            preview.map((fp) => (
              <Box key={fp.field.fieldName} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {fp.field.label} — Preview
                </Typography>
                {fp.sheetMissing ? (
                  <Alert severity="error">
                    Sheet "{fp.field.label}" not found in the uploaded file.
                  </Alert>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Locale</TableCell>
                          <TableCell>Language</TableCell>
                          <TableCell>Text</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fp.rows.map((row, idx) => (
                          <TableRow
                            key={`${row.languageCode || "unknown"}-${idx}`}
                            className={row.errors.length ? styles.errorRow : undefined}
                          >
                            <TableCell>{row.languageCode || "—"}</TableCell>
                            <TableCell>
                              {languageNameByCode[row.languageCode] || "—"}
                            </TableCell>
                            <TableCell>{row.description || "—"}</TableCell>
                            <TableCell>
                              {row.errors.length ? (
                                <Typography variant="caption" color="error">
                                  {row.errors.join("; ")}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="success.main">
                                  OK
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            ))}

          <Box className={styles.actions} sx={{ mt: 1 }}>
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            {!deferUpload && (
              <Button
                disabled={!file || disabled || loading || parsing || hasErrors}
                variant="contained"
                onClick={onUpload}
                startIcon={<UploadFileIcon />}
                size="large"
              >
                {loading ? "Updating..." : "Update"}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default BulkTranslationUpload;
